// apps/admin/src/api/catalog.api.ts
//
// Phase 8.2 Wave 6 Task 37 — Catalog shared admin API client.
//
// Decisions:
//   shared catalog API client + TanStack Query keys (this file).
//   admin cookie strategy (handled by apiClient — JWT pw_token
//              HTTP-only cookie + XSRF double-submit are auto-attached).
//
// Goals:
//    (Categories), (Blueprints), (Techniques)
//   (Product Templates).
//
// Endpoints (Phase 8.2 Wave 5 BE surface — commits 357b5eb0, 6db31ac1,
// ca8433c0, ab476541):
//
//   Categories                                 (BR-cat in commit 357b5eb0)
//     GET    /api/v1/admin/categories
//     POST   /api/v1/admin/categories
//     POST   /api/v1/admin/categories/reorder
//     PATCH  /api/v1/admin/categories/:id
//     DELETE /api/v1/admin/categories/:id
//
//   Blueprints                                 (commit 6db31ac1)
//     GET    /api/v1/admin/blueprints
//     PATCH  /api/v1/admin/blueprints/:id/variant-status
//     POST   /api/v1/admin/blueprints/import
//     GET    /api/v1/admin/blueprints/export
//     GET    /api/v1/admin/blueprints/duplicate-candidates
//
//   Product Templates                          (commit ab476541)
//     GET    /api/v1/admin/products/templates
//     GET    /api/v1/admin/products/templates/:id
//     POST   /api/v1/admin/products/templates
//     PUT    /api/v1/admin/products/templates/:id
//     DELETE /api/v1/admin/products/templates/:id
//     POST   /api/v1/admin/products/templates/:id/restore
//     POST   /api/v1/admin/products/templates/bulk-activate
//     POST   /api/v1/admin/products/templates/bulk-deactivate
//
//   Techniques (commit ca8433c0 / merged sidebar)
//     GET    /api/v1/admin/techniques
//     POST   /api/v1/admin/techniques
//     PATCH  /api/v1/admin/techniques/:id
//     DELETE /api/v1/admin/techniques/:id
//     PUT    /api/v1/admin/techniques/embroidery/stitch-ranges
//     PUT    /api/v1/admin/techniques/embroidery/digitizing-fee
//
// Envelope handling:
//   `apiClient` (apps/admin/src/lib/apiClient.ts — Phase 8.1 Wave 1) handles
//   response envelope unwrap and throws `ApiError` with the
//   INTERFACE-STANDARDS message priority chain
//   (`error.user_message > error.message > network_fallback`) — see
//   .vg/phases/08.1-admin-core/INTERFACE-STANDARDS.md §message_priority.
//   We never read AxiosError.message or HTTP statusText directly — the
//   thrown ApiError already carries the resolved user-facing copy.
//
// Idempotency-Key:
//   apiClient.post/put/patch/delete accept `{ idempotencyKey }` in opts;
//   each mutation generates a fresh UUID v4 via crypto.randomUUID(). DO
//   NOT cache the key — re-issuing the same key would short-circuit
//   retries with the cached prior response.

import {
  apiClient,
  type ApiRequestOptions,
} from '../lib/apiClient.js';
import type {
  AdminBlueprintListResponse,
  AdminCategoryItem,
  AdminTechniqueRow,
  AdminTemplateListResponse,
  AdminTemplateRow,
  BlueprintListFilters,
  CategoryListFilters,
  CreateCategoryBody,
  AdminTechniquePage,
  CreateTechniqueBody,
  ReorderCategoryItem,
  TechniqueListFilters,
  TemplateListFilters,
  UpdateBlueprintVariantStatusBody,
  UpdateCategoryBody,
  UpdateTechniqueBody,
} from '../types/catalog.js';

// ---------------------------------------------------------------------------
// Idempotency helper — fresh UUID v4 per mutation.
// ---------------------------------------------------------------------------

function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Defensive fallback for older browsers — apiClient interceptor still
  // attaches the header verbatim, so just produce a sufficiently-unique
  // string. crypto.randomUUID is available in every browser/runtime the
  // admin SPA targets (Vite 8 + ES2022).
  return `idemp-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function mutationOpts(extra?: ApiRequestOptions): ApiRequestOptions {
  return { idempotencyKey: newIdempotencyKey(), ...extra };
}

// ---------------------------------------------------------------------------
// Query-string builders — keep undefined params out so the BE Zod
// validator doesn't reject them (admin BE handlers use .strict() on
// several schemas).
// ---------------------------------------------------------------------------

function appendIfDefined(
  sp: URLSearchParams,
  key: string,
  value: string | number | boolean | null | undefined,
): void {
  if (value === undefined || value === null) return;
  sp.set(key, String(value));
}

function withQuery(path: string, sp: URLSearchParams): string {
  const qs = sp.toString();
  return qs ? `${path}?${qs}` : path;
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

function buildCategoryListQs(filters: CategoryListFilters): string {
  const sp = new URLSearchParams();
  appendIfDefined(sp, 'status', filters.status);
  // BE accepts the literal string 'null' to filter for top-level categories
  // (parentId === null), per category.admin.handler.ts ListCategoryQuery.
  if (filters.parentId === null) {
    sp.set('parentId', 'null');
  } else if (filters.parentId !== undefined) {
    sp.set('parentId', filters.parentId);
  }
  if (filters.topLevelOnly === true) sp.set('topLevelOnly', 'true');
  return sp.toString();
}

/** GET /api/v1/admin/categories — flat array (BE chooses ordering). */
export async function listAdminCategories(
  filters: CategoryListFilters = {},
): Promise<AdminCategoryItem[]> {
  const qs = buildCategoryListQs(filters);
  const path = qs
    ? `/admin/categories?${qs}`
    : '/admin/categories';
  return apiClient.get<AdminCategoryItem[]>(path);
}

/** POST /api/v1/admin/categories — create. Auto Idempotency-Key per call. */
export async function createAdminCategory(
  body: CreateCategoryBody,
): Promise<AdminCategoryItem> {
  return apiClient.post<AdminCategoryItem>(
    '/admin/categories',
    body,
    mutationOpts(),
  );
}

/** PATCH /api/v1/admin/categories/:id — partial update. */
export async function updateAdminCategory(
  id: string,
  body: UpdateCategoryBody,
): Promise<AdminCategoryItem> {
  return apiClient.patch<AdminCategoryItem>(
    `/admin/categories/${encodeURIComponent(id)}`,
    body,
    mutationOpts(),
  );
}

/** DELETE /api/v1/admin/categories/:id — soft-delete (blueprint guard). */
export async function deleteAdminCategory(id: string): Promise<void> {
  await apiClient.delete<unknown>(
    `/admin/categories/${encodeURIComponent(id)}`,
    mutationOpts(),
  );
}

/**
 * POST /api/v1/admin/categories/reorder — batch reorder within the same
 * sibling level. The BE rejects with 409 CATEGORY_REORDER_CROSS_LEVEL if
 * any item targets a different parent.
 */
export async function reorderAdminCategories(
  items: ReorderCategoryItem[],
): Promise<AdminCategoryItem[]> {
  return apiClient.post<AdminCategoryItem[]>(
    '/admin/categories/reorder',
    { items },
    mutationOpts(),
  );
}

// ---------------------------------------------------------------------------
// Blueprints
// ---------------------------------------------------------------------------

function buildBlueprintListQs(filters: BlueprintListFilters): string {
  const sp = new URLSearchParams();
  appendIfDefined(sp, 'page', filters.page);
  appendIfDefined(sp, 'pageSize', filters.pageSize);
  appendIfDefined(sp, 'status', filters.status);
  appendIfDefined(sp, 'source', filters.source);
  appendIfDefined(sp, 'search', filters.search);
  // `ids` is comma-joined, and an EMPTY array still sends `ids=` so the server
  // returns nothing — dropping the param would return every blueprint, which
  // is the opposite of "show only these".
  if (filters.ids !== undefined) sp.set('ids', filters.ids.join(','));
  return sp.toString();
}

/** GET /api/v1/admin/blueprints — paginated list with list columns. */
export async function listAdminBlueprints(
  filters: BlueprintListFilters = {},
): Promise<AdminBlueprintListResponse> {
  const qs = buildBlueprintListQs(filters);
  const path = qs ? `/admin/blueprints?${qs}` : '/admin/blueprints';
  const payload = await apiClient.get<AdminBlueprintListResponse>(path);
  // Defensive — BE always returns the envelope, but consumers expect a
  // stable shape even on empty result sets (no `undefined` fields).
  return {
    items: payload.items ?? [],
    pagination: payload.pagination ?? {
      page: 1,
      pageSize: filters.pageSize ?? 20,
      total: 0,
      totalPages: 0,
    },
  };
}

/**
 * PATCH /api/v1/admin/blueprints/:id/variant-status — apply 3-status
 * variant changes. Body shape:
 *   { variantStatuses: [{ sku, status: "In Stock"|"Out of Stock"|"Hidden" }] }
 * BE translates display strings to the internal Phase 2 enum.
 */
export async function updateBlueprintVariantStatus(
  blueprintId: string,
  body: UpdateBlueprintVariantStatusBody,
): Promise<unknown> {
  return apiClient.patch<unknown>(
    `/admin/blueprints/${encodeURIComponent(blueprintId)}/variant-status`,
    body,
    mutationOpts(),
  );
}

// ---------------------------------------------------------------------------
// Product Templates
// ---------------------------------------------------------------------------

function buildTemplateListQs(filters: TemplateListFilters): string {
  const sp = new URLSearchParams();
  appendIfDefined(sp, 'page', filters.page);
  appendIfDefined(sp, 'limit', filters.limit);
  appendIfDefined(sp, 'search', filters.search);
  appendIfDefined(sp, 'area', filters.area);
  appendIfDefined(sp, 'type', filters.type);
  appendIfDefined(sp, 'isActive', filters.isActive);
  appendIfDefined(sp, 'isDeleted', filters.isDeleted);
  return sp.toString();
}

/** GET /api/v1/admin/products/templates — paginated list. */
export async function listAdminTemplates(
  filters: TemplateListFilters = {},
): Promise<AdminTemplateListResponse> {
  const qs = buildTemplateListQs(filters);
  const path = qs
    ? `/admin/products/templates?${qs}`
    : '/admin/products/templates';
  const payload = await apiClient.get<AdminTemplateListResponse>(path);
  return {
    items: payload.items ?? [],
    page: payload.page ?? 1,
    limit: payload.limit ?? (filters.limit ?? 30),
    total: payload.total ?? 0,
    totalPages: payload.totalPages ?? 1,
  };
}

/** GET /api/v1/admin/products/templates/:id — fetch single template. */
export async function getAdminTemplate(id: string): Promise<AdminTemplateRow> {
  return apiClient.get<AdminTemplateRow>(
    `/admin/products/templates/${encodeURIComponent(id)}`,
  );
}

// ---------------------------------------------------------------------------
// Techniques
// ---------------------------------------------------------------------------

function buildTechniqueListQs(filters: TechniqueListFilters): string {
  const sp = new URLSearchParams();
  appendIfDefined(sp, 'type', filters.type);
  if (filters.includeInactive === true) sp.set('includeInactive', 'true');
  if (filters.search !== undefined && filters.search !== '') sp.set('search', filters.search);
  appendIfDefined(sp, 'status', filters.status);
  if (filters.page !== undefined) sp.set('page', String(filters.page));
  if (filters.pageSize !== undefined) sp.set('pageSize', String(filters.pageSize));
  return sp.toString();
}

/**
 * GET /api/v1/public/techniques — active print+embroidery techniques.
 * 2026-09-09 vendor-register redesign: feeds the create-vendor wizard
 * capability checkboxes (same source as the vendor register form). Public
 * endpoint (no permission needed — `products:technique:manage` is not
 * required just to CREATE a vendor).
 */
export interface PublicTechniqueOption {
  id: string;
  code: string;
  name: Record<string, string>;
  type: string;
}

export async function listPublicTechniques(): Promise<PublicTechniqueOption[]> {
  const res = await apiClient.get<{ techniques: PublicTechniqueOption[] }>(
    '/public/techniques',
  );
  return Array.isArray(res?.techniques) ? res.techniques : [];
}

/**
 * GET /api/v1/admin/techniques.
 * Returns flat array. Tolerant of BE envelope drift:
 *   - canonical: data: AdminTechniqueRow[]            (flat array)
 *   - actual BE: data: { items: AdminTechniqueRow[] } (wrapped)
 * dbg-20260520-021720 NEW-1 unwrap — apiClient strips top-level envelope
 * so we get either the array OR the { items } object.
 */
export async function listAdminTechniques(
  filters: TechniqueListFilters = {},
): Promise<AdminTechniqueRow[]> {
  const path = withQuery(
    '/admin/techniques',
    new URLSearchParams(buildTechniqueListQs(filters)),
  );
  const res = await apiClient.get<
    AdminTechniqueRow[] | { items: AdminTechniqueRow[] }
  >(path);
  if (Array.isArray(res)) return res;
  return Array.isArray((res as { items?: unknown }).items)
    ? ((res as { items: AdminTechniqueRow[] }).items)
    : [];
}

/**
 * GET /api/v1/admin/techniques with `pageSize` — the paged envelope the admin
 * list needs so search, the status filter and the pager are all server work
 * (dbg-20260806-095919 iteration 3). `listAdminTechniques` above stays as it
 * was for the callers that genuinely want every row.
 */
export async function listAdminTechniquesPaged(
  filters: TechniqueListFilters & { pageSize: number },
): Promise<AdminTechniquePage> {
  const path = withQuery(
    '/admin/techniques',
    new URLSearchParams(buildTechniqueListQs(filters)),
  );
  const res = await apiClient.get<Partial<AdminTechniquePage>>(path);
  const items = Array.isArray(res?.items) ? res.items : [];
  return {
    items,
    page: res?.page ?? filters.page ?? 1,
    pageSize: res?.pageSize ?? filters.pageSize,
    total: res?.total ?? items.length,
    totalPages: res?.totalPages ?? 1,
  };
}

/** POST /api/v1/admin/techniques — create. */
export async function createAdminTechnique(
  body: CreateTechniqueBody,
): Promise<AdminTechniqueRow> {
  return apiClient.post<AdminTechniqueRow>(
    '/admin/techniques',
    body,
    mutationOpts(),
  );
}

/** PATCH /api/v1/admin/techniques/:id — strict partial update. */
export async function updateAdminTechnique(
  id: string,
  body: UpdateTechniqueBody,
): Promise<AdminTechniqueRow> {
  return apiClient.patch<AdminTechniqueRow>(
    `/admin/techniques/${encodeURIComponent(id)}`,
    body,
    mutationOpts(),
  );
}

/** DELETE /api/v1/admin/techniques/:id — soft-delete (isActive=false). */
export async function deleteAdminTechnique(id: string): Promise<void> {
  await apiClient.delete<unknown>(
    `/admin/techniques/${encodeURIComponent(id)}`,
    mutationOpts(),
  );
}

// ---------------------------------------------------------------------------
// Print areas — the option list behind per-area embroidery pricing
//   GET /api/v1/admin/print-areas
// ---------------------------------------------------------------------------

/** One row of the print-area catalog (`print_areas`). Seeded: front, back. */
export interface AdminPrintAreaRow {
  id: string;
  /** Machine identity referenced by technique.embroideryAreas[].areaCode. */
  code: string;
  label: string;
  archived: boolean;
  sortOrder: number;
}

/**
 * GET /api/v1/admin/print-areas — active areas, in display order.
 *
 * Archived rows are dropped: they must not appear as new choices, but a
 * technique that already prices one keeps its entry (areaCode is a soft ref).
 */
export async function listAdminPrintAreas(): Promise<AdminPrintAreaRow[]> {
  // `?active=true` filters archived rows server-side. Archived areas must not
  // appear as new choices, while a technique that already prices one keeps its
  // entry (areaCode is a soft ref).
  const rows = await apiClient.get<AdminPrintAreaRow[]>(
    '/admin/print-areas?active=true',
  );
  // The BE sorts by (sortOrder, code), but the seeded rows both carry the
  // sortOrder they were minted with — back=0, front=1 — which puts Back first.
  // Front reads first everywhere in the product (and in the design), so the
  // canonical pair is pinned ahead of whatever else the catalog holds.
  const RANK: Record<string, number> = { front: 0, back: 1 };
  const rank = (code: string): number => RANK[code] ?? 2;
  return [...rows].sort(
    (a, b) =>
      rank(a.code) - rank(b.code) ||
      a.sortOrder - b.sortOrder ||
      a.code.localeCompare(b.code),
  );
}
